import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Users, Search, Filter, Eye, Edit, UserX, Ban, DollarSign, Trash2, 
  X, Check, AlertTriangle, Shield, Clock, UserCheck, UserMinus,
  Phone, Mail, Globe, Calendar, Wallet, TrendingUp, TrendingDown, Loader2
} from 'lucide-react';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [balanceModalOpen, setBalanceModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [balanceForm, setBalanceForm] = useState({ amount: '', adjustment_type: 'add', reason: '' });
  const [actionLoading, setActionLoading] = useState(false);
  
  // User details state
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await adminAPI.getUsers(params);
      setUsers(response.data.users || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, statusFilter]);

  const fetchUserDetails = async (userId) => {
    try {
      setDetailsLoading(true);
      const response = await adminAPI.getUser(userId);
      setUserDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch user details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    fetchUserDetails(user.id);
    setViewModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      country: user.country || '',
      status: user.status || 'active',
      tier: user.tier || 'basic'
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true);
      await adminAPI.updateUserFull(selectedUser.id, editForm);
      toast.success('User updated successfully');
      setEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjustBalance = (user) => {
    setSelectedUser(user);
    setBalanceForm({ amount: '', adjustment_type: 'add', reason: '' });
    setBalanceModalOpen(true);
  };

  const handleSaveBalance = async () => {
    if (!balanceForm.amount || parseFloat(balanceForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      setActionLoading(true);
      await adminAPI.adjustBalance(selectedUser.id, {
        amount: parseFloat(balanceForm.amount),
        adjustment_type: balanceForm.adjustment_type,
        reason: balanceForm.reason
      });
      toast.success('Balance adjusted successfully');
      setBalanceModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust balance');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = (user, newStatus) => {
    setSelectedUser(user);
    setConfirmAction({ type: newStatus, label: newStatus === 'suspended' ? 'Suspend' : 'Ban' });
    setConfirmModalOpen(true);
  };

  const executeStatusChange = async () => {
    try {
      setActionLoading(true);
      await adminAPI.updateUser(selectedUser.id, { status: confirmAction.type });
      toast.success(`User ${confirmAction.label.toLowerCase()}ed successfully`);
      setConfirmModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(`Failed to ${confirmAction.label.toLowerCase()} user`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const executeDelete = async () => {
    try {
      setActionLoading(true);
      await adminAPI.deleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      setDeleteModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      unverified: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      suspended: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      banned: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[status] || styles.pending;
  };

  const statCards = [
    { label: 'Total Users', value: stats.total || 0, icon: Users, color: 'from-cyan-500 to-blue-500' },
    { label: 'Active', value: stats.active || 0, icon: UserCheck, color: 'from-emerald-500 to-green-500' },
    { label: 'Unverified', value: stats.unverified || 0, icon: Clock, color: 'from-blue-500 to-indigo-500' },
    { label: 'Pending', value: stats.pending || 0, icon: Clock, color: 'from-yellow-500 to-orange-500' },
    { label: 'Suspended', value: stats.suspended || 0, icon: UserMinus, color: 'from-orange-500 to-red-500' },
    { label: 'Banned', value: stats.banned || 0, icon: Ban, color: 'from-red-500 to-pink-500' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-gray-400 text-sm mt-1">Manage and monitor all platform users</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4"
          >
            <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${stat.color} flex items-center justify-center mb-3`}>
              <stat.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-xs text-gray-400">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, phone, or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
            data-testid="user-search-input"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-10 pr-8 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500 transition-colors min-w-[150px]"
            data-testid="status-filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="unverified">Unverified</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
          </select>
        </div>
      </div>

      {/* Users Table - Desktop */}
      <div className="hidden lg:block bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Country</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Balance</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Registered</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
                    <p className="text-gray-400 mt-2">Loading users...</p>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No users found</p>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold">
                          {user.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name || 'Unknown'}</p>
                          <p className="text-gray-400 text-sm">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300 text-sm">{user.phone || '-'}</td>
                    <td className="px-6 py-4 text-gray-300 text-sm">{user.country || '-'}</td>
                    <td className="px-6 py-4">
                      <span className="text-emerald-400 font-semibold">${(user.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(user.status)}`}>
                        {user.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="p-2 text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors"
                          title="View Details"
                          data-testid={`view-user-${user.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                          title="Edit User"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleAdjustBalance(user)}
                          className="p-2 text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
                          title="Adjust Balance"
                          data-testid={`balance-user-${user.id}`}
                        >
                          <DollarSign className="w-4 h-4" />
                        </button>
                        {user.status !== 'suspended' && (
                          <button
                            onClick={() => handleStatusChange(user, 'suspended')}
                            className="p-2 text-gray-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
                            title="Suspend User"
                            data-testid={`suspend-user-${user.id}`}
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                        {user.status !== 'banned' && (
                          <button
                            onClick={() => handleStatusChange(user, 'banned')}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Ban User"
                            data-testid={`ban-user-${user.id}`}
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete User"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Users Cards - Mobile/Tablet */}
      <div className="lg:hidden space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
            <p className="text-gray-400 mt-2">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No users found</p>
          </div>
        ) : (
          users.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold text-lg">
                    {user.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{user.name || 'Unknown'}</p>
                    <p className="text-gray-400 text-sm">{user.email}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(user.status)}`}>
                  {user.status?.toUpperCase() || 'UNKNOWN'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="text-gray-300">{user.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Country</p>
                  <p className="text-gray-300">{user.country || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Balance</p>
                  <p className="text-emerald-400 font-semibold">${(user.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <p className="text-gray-500">Registered</p>
                  <p className="text-gray-300">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-700/50">
                <button
                  onClick={() => handleViewUser(user)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-gray-700/50 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                >
                  <Eye className="w-4 h-4" /> View
                </button>
                <button
                  onClick={() => handleEditUser(user)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => handleAdjustBalance(user)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
                >
                  <DollarSign className="w-4 h-4" /> Balance
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {user.status !== 'suspended' && (
                  <button
                    onClick={() => handleStatusChange(user, 'suspended')}
                    className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg transition-colors"
                  >
                    <UserX className="w-4 h-4" /> Suspend
                  </button>
                )}
                {user.status !== 'banned' && (
                  <button
                    onClick={() => handleStatusChange(user, 'banned')}
                    className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                  >
                    <Ban className="w-4 h-4" /> Ban
                  </button>
                )}
                <button
                  onClick={() => handleDeleteUser(user)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* View User Modal */}
      <AnimatePresence>
        {viewModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setViewModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-gray-800 z-10">
                <h2 className="text-xl font-bold text-white">User Details</h2>
                <button
                  onClick={() => setViewModalOpen(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              {detailsLoading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mx-auto" />
                </div>
              ) : userDetails ? (
                <div className="p-6 space-y-6">
                  {/* User Info */}
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold text-2xl">
                      {userDetails.user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{userDetails.user?.name}</h3>
                      <p className="text-gray-400">{userDetails.user?.email}</p>
                      <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(userDetails.user?.status)}`}>
                        {userDetails.user?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Contact & Account Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-700/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                        <Phone className="w-4 h-4" /> Phone
                      </div>
                      <p className="text-white">{userDetails.user?.phone || '-'}</p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                        <Globe className="w-4 h-4" /> Country
                      </div>
                      <p className="text-white">{userDetails.user?.country || '-'}</p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                        <Calendar className="w-4 h-4" /> Registered
                      </div>
                      <p className="text-white">
                        {userDetails.user?.created_at ? new Date(userDetails.user.created_at).toLocaleString() : '-'}
                      </p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                        <Shield className="w-4 h-4" /> Tier
                      </div>
                      <p className="text-white capitalize">{userDetails.user?.tier || 'basic'}</p>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Financial Summary</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4 text-center">
                        <Wallet className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">${(userDetails.user?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-gray-400">Current Balance</p>
                      </div>
                      <div className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 border border-emerald-500/30 rounded-xl p-4 text-center">
                        <TrendingUp className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">${(userDetails.summary?.total_deposits || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-gray-400">Total Deposits</p>
                      </div>
                      <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-xl p-4 text-center">
                        <TrendingDown className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">${(userDetails.summary?.total_withdrawals || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-gray-400">Total Withdrawals</p>
                      </div>
                      <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4 text-center">
                        <DollarSign className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">${(userDetails.user?.total_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-gray-400">Total Profit</p>
                      </div>
                    </div>
                  </div>

                  {/* Transaction Counts */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    <div className="bg-gray-700/30 rounded-xl p-3">
                      <p className="text-xl font-bold text-white">{userDetails.summary?.deposits_count || 0}</p>
                      <p className="text-xs text-gray-400">Approved Deposits</p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-3">
                      <p className="text-xl font-bold text-white">{userDetails.summary?.pending_deposits || 0}</p>
                      <p className="text-xs text-gray-400">Pending Deposits</p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-3">
                      <p className="text-xl font-bold text-white">{userDetails.summary?.withdrawals_count || 0}</p>
                      <p className="text-xs text-gray-400">Completed Withdrawals</p>
                    </div>
                    <div className="bg-gray-700/30 rounded-xl p-3">
                      <p className="text-xl font-bold text-white">{userDetails.summary?.pending_withdrawals || 0}</p>
                      <p className="text-xs text-gray-400">Pending Withdrawals</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setEditModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white">Edit User</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Full Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                    data-testid="edit-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Phone</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                    data-testid="edit-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Country</label>
                  <input
                    type="text"
                    value={editForm.country}
                    onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                    data-testid="edit-country-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                    data-testid="edit-status-select"
                  >
                    <option value="active">Active</option>
                    <option value="unverified">Unverified</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                    <option value="banned">Banned</option>
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-xl transition-all flex items-center justify-center gap-2"
                  data-testid="save-edit-btn"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Adjust Balance Modal */}
      <AnimatePresence>
        {balanceModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setBalanceModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white">Adjust Balance</h2>
                <p className="text-gray-400 text-sm mt-1">
                  {selectedUser?.name}'s current balance: <span className="text-emerald-400 font-semibold">${(selectedUser?.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Adjustment Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setBalanceForm({ ...balanceForm, adjustment_type: 'add' })}
                      className={`py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                        balanceForm.adjustment_type === 'add'
                          ? 'bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400'
                          : 'bg-gray-700/50 border-2 border-transparent text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      <TrendingUp className="w-5 h-5" /> Add Funds
                    </button>
                    <button
                      onClick={() => setBalanceForm({ ...balanceForm, adjustment_type: 'subtract' })}
                      className={`py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                        balanceForm.adjustment_type === 'subtract'
                          ? 'bg-red-500/20 border-2 border-red-500 text-red-400'
                          : 'bg-gray-700/50 border-2 border-transparent text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      <TrendingDown className="w-5 h-5" /> Subtract
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Amount ($)</label>
                  <input
                    type="number"
                    value={balanceForm.amount}
                    onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white text-xl font-semibold focus:outline-none focus:border-cyan-500"
                    data-testid="balance-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Reason / Note</label>
                  <textarea
                    value={balanceForm.reason}
                    onChange={(e) => setBalanceForm({ ...balanceForm, reason: e.target.value })}
                    placeholder="Enter reason for adjustment..."
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white resize-none focus:outline-none focus:border-cyan-500"
                    data-testid="balance-reason-input"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setBalanceModalOpen(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBalance}
                  disabled={actionLoading}
                  className={`flex-1 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                    balanceForm.adjustment_type === 'add'
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                  } text-white`}
                  data-testid="confirm-balance-btn"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  Confirm Adjustment
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Status Change Modal */}
      <AnimatePresence>
        {confirmModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setConfirmModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  confirmAction?.type === 'suspended' ? 'bg-orange-500/20' : 'bg-red-500/20'
                }`}>
                  <AlertTriangle className={`w-8 h-8 ${
                    confirmAction?.type === 'suspended' ? 'text-orange-400' : 'text-red-400'
                  }`} />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">{confirmAction?.label} User?</h2>
                <p className="text-gray-400">
                  Are you sure you want to {confirmAction?.label.toLowerCase()} <span className="text-white font-semibold">{selectedUser?.name}</span>?
                  {confirmAction?.type === 'suspended' && ' They will not be able to log in or perform actions.'}
                  {confirmAction?.type === 'banned' && ' They will be permanently blocked from accessing the platform.'}
                </p>
              </div>
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setConfirmModalOpen(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeStatusChange}
                  disabled={actionLoading}
                  className={`flex-1 py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-white ${
                    confirmAction?.type === 'suspended'
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
                      : 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600'
                  }`}
                  data-testid="confirm-status-btn"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  Yes, {confirmAction?.label}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete User Modal */}
      <AnimatePresence>
        {deleteModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-red-500/20 mx-auto mb-4 flex items-center justify-center">
                  <Trash2 className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Delete User?</h2>
                <p className="text-gray-400">
                  This action cannot be undone. All data for <span className="text-white font-semibold">{selectedUser?.name}</span> will be permanently removed.
                </p>
              </div>
              <div className="p-6 border-t border-gray-700 flex gap-3">
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeDelete}
                  disabled={actionLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-xl transition-all flex items-center justify-center gap-2"
                  data-testid="confirm-delete-btn"
                >
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
